/**
 * Dilbilimsel muhakeme: şifreli dil ve mantıksal çelişki.
 *
 * İkisi de okuduğunu anlamanın üstüne bir şey daha istiyor — biri
 * kuralı tümevarımla çıkarmayı, öteki metni kendi içinde tutarlılık
 * açısından denetlemeyi.
 *
 * İKİSİ DE DİL MODELİ KULLANMIYOR. İstek listesinde "yapay zeka ile
 * sonsuz senaryo" yazıyordu; puanlama sunucuda ve determinist olmak
 * zorunda, aynı cevap iki kez aynı puanı almalı. Üretim tarafında da
 * aynı sorun var: aynı tohum aynı sayfayı vermezse "kaybolan ödevi
 * yeniden bas" sözü düşer. İkisi de şablondan üretiliyor; varyant
 * sayısı sonsuz değil ama binlerce ve hepsi denetlenebilir.
 */

import { Rng } from '../../prng'
import { ayrilma, basHarf, bulunma, cogul, foldTr, gradeList, gradeSet } from '../helpers'
import { EXERCISE_VERSION, clampLevel, scale } from '../types'
import type { ExerciseTemplate, RenderNode } from '../types'

/* ================================================================== *
 * 32. Şifreli dil çözümleme
 *
 * Uydurma bir dilin birkaç kelimesi ve Türkçe karşılığı veriliyor;
 * çocuk kuralı çıkarıp yeni bir kelimeyi kuruyor.
 *
 * SORULAN KELİMENİN HER PARÇASI ÖRNEKLERDE GEÇMEK ZORUNDA. Geçmezse
 * cevap tümevarımla değil tahminle veriliyor ve yanlış cevap çocuğun
 * değil sayfanın hatası oluyor. Üretim bunu her soru için denetliyor.
 *
 * Cevap ekranda hecelerden DOKUNARAK kuruluyor. Uydurma bir dilin
 * hecelerini tablet klavyesinde yazmak, ölçtüğümüz şeye bambaşka bir
 * yük ekliyor: kuralı bulan ama yazamayan çocuk yanlış görünüyor.
 * ================================================================== */
export const codeLanguage: ExerciseTemplate = {
  code: 'code_language',
  title: 'Şifreli dil',
  domains: ['working_memory_verbal', 'cognitive_flexibility'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return {
      /** Kaç nesne (kök) */
      kok: scale(l, 2, 4),
      /** Kaç niteleyici (ön ek) */
      nitel: scale(l, 2, 4),
      /** Kaç soru */
      soru: scale(l, 2, 4),
      /** Kaç örnek gösterilecek */
      ornek: scale(l, 3, 6),
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const kokSayisi = p['kok'] as number
    const nitelSayisi = p['nitel'] as number

    const HECELER = ['ko', 'ta', 'ma', 'lu', 'ne', 'si', 'ro', 'vi', 'pe', 'da', 'gu', 'ze']
    const NESNELER = ['ev', 'araba', 'kuş', 'top', 'kapı', 'ağaç']
    const NITELER = ['kırmızı', 'mavi', 'büyük', 'küçük', 'eski', 'yeni']

    const heceler = rng.shuffle(HECELER)
    const kokler = rng.shuffle(NESNELER).slice(0, kokSayisi)
    const niteller = rng.shuffle(NITELER).slice(0, nitelSayisi)
    const kokHece = new Map(kokler.map((k, i) => [k, heceler[i]!]))
    const nitelHece = new Map(niteller.map((n, i) => [n, heceler[kokSayisi + i]!]))

    /** Kural: önce niteleyici, sonra nesne. Tireyle bağlanıyor. */
    const kelime = (nitel: string, kok: string) => `${nitelHece.get(nitel)}-${kokHece.get(kok)}`
    const turkce = (nitel: string, kok: string) => `${nitel} ${kok}`

    const tumIkililer: [string, string][] = []
    for (const n of niteller) for (const k of kokler) tumIkililer.push([n, k])
    const karisik = rng.shuffle(tumIkililer)

    /**
     * Örnekler: her kök ve her niteleyici en az bir kez geçecek.
     *
     * Geçmeseydi o heceyi hiç görmemiş bir çocuktan onu kullanmasını
     * istemiş olurduk.
     */
    const ornekler: [string, string][] = []
    const gorulenKok = new Set<string>()
    const gorulenNitel = new Set<string>()
    for (const [n, k] of karisik) {
      if (gorulenKok.has(k) && gorulenNitel.has(n)) continue
      ornekler.push([n, k])
      gorulenKok.add(k)
      gorulenNitel.add(n)
    }
    // Kalan örnek bütçesi: rastgele doldur
    for (const ikili of karisik) {
      if (ornekler.length >= (p['ornek'] as number)) break
      if (ornekler.some(([n, k]) => n === ikili[0] && k === ikili[1])) continue
      ornekler.push(ikili)
    }

    const sorular = karisik
      .filter(([n, k]) => !ornekler.some(([on, ok]) => on === n && ok === k))
      .slice(0, p['soru'] as number)

    const sozluk = ornekler
      .map(([n, k]) => `"${kelime(n, k)}" = ${turkce(n, k)}`)
      .join('   ·   ')

    return {
      code: 'code_language',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt: 'Bu uydurma dilin kuralını bul. Aşağıdakiler bu dilde nasıl söylenir?',
      spokenPrompt:
        'Sana uydurma bir dilin birkaç kelimesini vereceğim. Kuralı bulup yeni kelimeler kuracaksın.',
      render: [
        { t: 'text', value: sozluk, size: 'md' },
        {
          t: 'rows',
          items: sorular.map(([n, k], i) => ({
            label: turkce(n, k),
            blank: true,
            id: 'c' + i,
          })),
          columns: 1,
          /** Heceler paletten: uydurma dili klavyede yazmak ayrı bir yük */
          palette: [...kokHece.values(), ...nitelHece.values()].map((h) => ({ glyph: h })),
          paletteMode: 'sequence',
        },
      ],
      solution: sorular.map(([n, k]) => kelime(n, k)),
      grading: 'auto',
      estimatedSeconds: 40 + sorular.length * 25,
      itemCount: sorular.length,
    }
  },
  /**
   * Karşılaştırma tire ve boşluktan bağımsız.
   *
   * Kâğıtta çocuk "lu-ma" yazıyor, ekranda paletten "lu ma" çıkıyor;
   * ikisi aynı cevap. Ayracı puanlamaya karıştırmak, kuralı bulan
   * çocuğu biçim yüzünden yanlış saymak olurdu.
   */
  grade(instance, answer) {
    const sade = (x: unknown) => foldTr(String(x ?? '')).replace(/[\s-]+/g, '')
    const beklenen = (instance.solution as string[]).map(sade)
    const verilen = (Array.isArray(answer) ? answer : []).map(sade)
    const temel = gradeList(beklenen, verilen)
    // Okunur beklenen: anahtarda sade hali değil, tireli hali dursun
    return {
      ...temel,
      items: temel.items?.map((it, i) => ({
        ...it,
        beklenen: (instance.solution as string[])[i] ?? it.beklenen,
      })),
    }
  },
  anahtar(instance) {
    return (instance.solution as string[]).map((x, i) => `${i + 1}. ${x}`).join('   ')
  },
}

/* ================================================================== *
 * 33. Mantıksal çelişki bulma
 *
 * Bir metinde iki cümle birbiriyle çelişiyor; çocuk onları buluyor.
 *
 * ÇELİŞEN TEK ÇİFT OLMAK ZORUNDA. Dolgu cümleleri birbiriyle de
 * çelişmemeli, yoksa sayfada iki doğru cevap olur ve çocuğun bulduğu
 * ikinci çift yanlış sayılır. Burada bu, her dolgu cümlesinin KENDİ
 * öznesinden söz etmesiyle sağlanıyor: ayrı özneler hakkındaki iki
 * cümle çelişemez.
 * ================================================================== */
export const contradiction: ExerciseTemplate = {
  code: 'contradiction',
  title: 'Çelişkiyi bul',
  domains: ['working_memory_verbal', 'selective_attention'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return {
      /** Toplam cümle sayısı */
      cumle: scale(l, 5, 10),
      /** Çelişki türü: 0 sayı, 1 karşılaştırma, 2 genelleme */
      tur: l >= 7 ? 2 : l >= 4 ? 1 : 0,
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const toplam = p['cumle'] as number
    const tur = p['tur'] as number

    /**
     * Yer sayısı cümle sayısından FAZLA olmak zorunda.
     *
     * Dolgu cümlelerinin çelişmemesinin tek güvencesi ayrı özneler:
     * aynı yer iki kez geçerse iki farklı adet yazılır ve sayfada
     * ikinci bir çelişki belirir. On cümlelik sayfada sekiz yer
     * yetmiyordu ve dizi taşıyordu.
     */
    const YERLER = [
      'raf', 'çanta', 'kutu', 'sepet', 'masa', 'dolap', 'sandık', 'çekmece',
      'valiz', 'poşet', 'kavanoz', 'torba',
    ]
    const ESYALAR = ['kalem', 'kitap', 'bilye', 'defter', 'bardak', 'tarak', 'anahtar', 'silgi']
    const ISIMLER = ['Ali', 'Ayşe', 'Mert', 'Zeynep', 'Can', 'Elif', 'Deniz', 'Kaan', 'Buse', 'Ömer']
    const RENKLER = ['mavi', 'kırmızı', 'yeşil', 'sarı']

    const yerler = rng.shuffle(YERLER)
    const esyalar = rng.shuffle(ESYALAR)
    const isimler = rng.shuffle(ISIMLER)

    const cumleler: string[] = []
    let celisen: [number, number]

    if (tur === 0) {
      // Sayı çelişkisi: aynı yerde iki farklı adet
      const yer = yerler[0]!
      const esya = esyalar[0]!
      const a = rng.int(3, 9)
      let b = rng.int(3, 9)
      while (b === a) b = rng.int(3, 9)
      cumleler.push(
        `${basHarf(bulunma(yer))} ${a} ${esya} var.`,
        `${basHarf(bulunma(yer))} ${b} ${esya} var.`,
      )
      celisen = [0, 1]
      // Dolgular: BAŞKA yerler, başka eşyalar — ayrı özneler çelişemez
      for (let i = 2; i < toplam; i++) {
        cumleler.push(
          `${basHarf(bulunma(yerler[i % yerler.length]!))} ${rng.int(2, 9)} ${esyalar[i % esyalar.length]!} var.`,
        )
      }
    } else if (tur === 1) {
      // Karşılaştırma çelişkisi: iki yönde birden
      const [x, y] = [isimler[0]!, isimler[1]!]
      cumleler.push(`${x}, ${ayrilma(y, true)} uzun.`, `${y}, ${ayrilma(x, true)} uzun.`)
      celisen = [0, 1]
      /**
       * Dolgular GERÇEK bir sıralamadan türetiliyor.
       *
       * İlk hali rastgele ikililer kuruyordu ve "A, B'den uzun" türü
       * cümleler kendi aralarında bir döngü oluşturabiliyordu
       * (A>B, B>C, C>A): sayfada ikinci bir çelişki belirir ve çocuğun
       * bulduğu doğru çift yanlış sayılırdı. Artık gizli bir boy
       * sıralaması var ve her dolgu o sıralamanın doğru bir ifadesi.
       *
       * Çelişen çiftin iki adı dolgularda HİÇ geçmiyor: geçseydi
       * çelişki ikiliden üçlüye çıkar ve cevap iki numara olmaktan
       * çıkardı.
       */
      const kalanlar = isimler.slice(2)
      for (let i = 2; i < toplam; i++) {
        const a = kalanlar[(i - 2) % kalanlar.length]!
        const b = kalanlar[(i - 1) % kalanlar.length]!
        // Sıralamada önce gelen daha uzun: her cümle doğru
        const once = kalanlar.indexOf(a) < kalanlar.indexOf(b) ? [a, b] : [b, a]
        cumleler.push(`${once[0]}, ${ayrilma(once[1]!, true)} uzun.`)
      }
    } else {
      // Genelleme çelişkisi: hepsi + bir istisna
      const yer = yerler[0]!
      const esya = esyalar[0]!
      const renk = rng.pick(RENKLER)
      let baska = rng.pick(RENKLER)
      while (baska === renk) baska = rng.pick(RENKLER)
      cumleler.push(
        `${basHarf(bulunma(yer))}ki ${cogul(esya)} ${renk}.`,
        `${basHarf(bulunma(yer))} bir ${baska} ${esya} var.`,
      )
      celisen = [0, 1]
      for (let i = 2; i < toplam; i++) {
        cumleler.push(
          `${basHarf(bulunma(yerler[i % yerler.length]!))} ${rng.int(2, 6)} ${esyalar[i % esyalar.length]!} var.`,
        )
      }
    }

    // Çelişen çifti metnin içine dağıt
    const sira = rng.shuffle(cumleler.map((_, i) => i))
    const yeniYer = new Map(sira.map((eski, yeni) => [eski, yeni]))
    const metin = sira.map((eski) => cumleler[eski]!)
    const cevap = celisen.map((i) => String(yeniYer.get(i)! + 1)).sort()

    const render: RenderNode[] = [
      {
        t: 'text',
        value: metin.map((c, i) => `${i + 1}. ${c}`).join('  '),
        size: 'md',
      },
      {
        t: 'rows',
        /* Kısa etiketler: uzun etiket iki sütunlu satırda sarıp kutuyu eziyordu */
        items: [
          { label: 'Birinci numara', blank: true, id: 'a' },
          { label: 'İkinci numara', blank: true, id: 'b' },
        ],
        columns: 2,
        palette: metin.map((_, i) => ({ glyph: String(i + 1) })),
      },
    ]

    return {
      code: 'contradiction',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt:
        'Metindeki cümlelerden ikisi birbiriyle çelişiyor. Onları bul ve numaralarını yaz.',
      spokenPrompt:
        'Metni okurken iki cümlenin aynı anda doğru olamayacağını fark edeceksin. Onların numaralarını yazacaksın.',
      render,
      solution: cevap,
      grading: 'auto',
      estimatedSeconds: 30 + toplam * 12,
      itemCount: 2,
    }
  },
  /** Sıra önemsiz: hangi numarayı hangi kutuya yazdığı bir şey anlatmıyor. */
  grade(instance, answer) {
    return gradeSet(instance.solution as string[], answer)
  },
  anahtar(instance) {
    return `Çelişen cümleler: ${(instance.solution as string[]).join(' ve ')}`
  },
}
